import { flash } from "@xola/ui-kit";
import dayjs from "dayjs";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { feedbackApi } from "../../api/feedbackApi";
import { useFeedbackSystemInfo } from "../../hooks/useFeedbackSystemInfo";
import { useFeedbackStore } from "../../stores/feedbackStore";
import { BugModal } from "./Bug";
import { FeatureModal } from "./Feature";
import { Feedback, Type } from "./Feedback";
import FeedbackButton from "./FeedbackButton";

interface FeedbackProps {
    className?: string;
}

export const FeedbackWidget = ({ className }: FeedbackProps) => {
    const [showFeatureModal, setShowFeatureModal] = useState(false);
    const [showBugModal, setShowBugModal] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const reset = useFeedbackStore((state) => state.reset);
    const sysInfo = useFeedbackSystemInfo();

    const resetData = (type: Type) => {
        setError(undefined);
        reset(type);
    };

    const handleFeature = () => {
        resetData(Type.FEATURE);
        setShowFeatureModal(true);
    };

    const handleBug = () => {
        resetData(Type.BUG);
        setShowBugModal(true);
    };

    const handleSend = async () => {
        const postData = cloneDeep(useFeedbackStore.getState().feedback);

        if (!postData.problem.trim()) {
            setError("Problem Description is required.");
            return;
        }

        postData.sellerId = sysInfo.seller.id;
        postData.reportedAt = new Date();
        const { clientInfo } = postData;
        const { appInfo, os, seller, userAgent, fullStorySessionUrl, sumoLogSessionId, desktop } = sysInfo;
        clientInfo.appName = "web";
        clientInfo.appVersion = appInfo.version;
        clientInfo.environment = appInfo.environment;
        clientInfo.installDate = appInfo.releaseDate;
        clientInfo.operatingSystem = `${os.name} ${os.version}`;
        clientInfo.timezone = seller.timezoneName ? seller.timezoneName : dayjs.tz.guess();
        clientInfo.userAgent = userAgent;
        clientInfo.fullStorySessionUrl = fullStorySessionUrl;
        clientInfo.sumoLogSessionId = sumoLogSessionId;
        clientInfo.screenResolution = desktop;
        setShowFeatureModal(false);
        setShowBugModal(false);
        // handle actual data sending in the background
        // otherwise modal dialog prevents user to do anything else while waiting response from Feedback API.
        // AWS Lambda service is slow to start, causing lot of delay.
        postToApi(postData);
    };

    return (
        <div className={className}>
            <FeedbackButton onFeatureClick={handleFeature} onBugClick={handleBug} />
            <FeatureModal
                isOpen={showFeatureModal}
                error={error}
                onClose={() => setShowFeatureModal(false)}
                onSend={handleSend}
            />
            <BugModal isOpen={showBugModal} error={error} onClose={() => setShowBugModal(false)} onSend={handleSend} />
        </div>
    );
};

const postToApi = async (postData: Feedback) => {
    await feedbackApi.post("", postData);
    flash.success(
        postData.type === Type.BUG
            ? "Your bug was successfully submitted. Our team will be in touch."
            : "Thank you! Your product feedback was successfully submitted. A Xola team member will be in touch for further clarification.",
    );
};
